import type { DemoSlot } from './demoTypes';

const SLOTS_KEY = 'sigma-demo-slots';
const ACTIVE_SLOT_KEY = 'sigma-demo-active-slot';

function generateUserId(): string {
  // Genera un GUID v4 estable que imita un AAD Object ID.
  //
  // Usa el generador criptográfico del navegador y no `Math.random()`. No es que este id
  // necesite ser impredecible —identifica al usuario de demo y nada más—, sino que
  // `Math.random()` aquí disparaba 8 alertas «alta» de CodeQL (js/insecure-randomness) que
  // inflaban el tablero de seguridad y tapaban los hallazgos que sí importan.
  //
  // Se arma con `getRandomValues` en vez del más corto `crypto.randomUUID()` a propósito:
  // esta función se evalúa al IMPORTAR el módulo (DEFAULT_USER / DEFAULT_SLOTS), y
  // `randomUUID` solo existe en contextos seguros. Servido desde un origen `http://` sería
  // `undefined` y la excepción tumbaría la carga del módulo entero. `getRandomValues` está
  // disponible también fuera de contexto seguro.
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // versión 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variante RFC 4122
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

const DEFAULT_SLOTS: DemoSlot[] = [
  { userId: generateUserId(), nombre: 'Usuario 1', correo: 'user1@sigma.demo', ouId: '', tenant: 'LATAM' },
  { userId: generateUserId(), nombre: 'Usuario 2', correo: 'user2@sigma.demo', ouId: '', tenant: 'LATAM' },
  { userId: generateUserId(), nombre: 'Usuario 3', correo: 'user3@sigma.demo', ouId: '', tenant: 'LATAM' },
  { userId: generateUserId(), nombre: 'Usuario 4', correo: 'user4@sigma.demo', ouId: '', tenant: 'LATAM' },
];

export function loadSlots(): DemoSlot[] {
  try {
    const raw = localStorage.getItem(SLOTS_KEY);
    if (!raw) return initSlots();
    const parsed = JSON.parse(raw) as unknown[];
    if (!Array.isArray(parsed) || parsed.length !== 4) return initSlots();
    return parsed.map((s, i) => sanitizeSlot(s, i));
  } catch {
    return initSlots();
  }
}

function initSlots(): DemoSlot[] {
  const slots = DEFAULT_SLOTS.map(s => ({ ...s, userId: generateUserId() }));
  saveSlots(slots);
  return slots;
}

function sanitizeSlot(raw: unknown, index: number): DemoSlot {
  const s = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
  return {
    userId:  typeof s['userId'] === 'string' && s['userId'] ? s['userId'] : generateUserId(),
    nombre:  typeof s['nombre'] === 'string' ? s['nombre'] : `Usuario ${index + 1}`,
    correo:  typeof s['correo'] === 'string' ? s['correo'] : `user${index + 1}@sigma.demo`,
    ouId:    typeof s['ouId'] === 'string' ? s['ouId'] : '',
    tenant:  s['tenant'] === 'Europa' ? 'Europa' : 'LATAM',
  };
}

export function saveSlots(slots: DemoSlot[]): void {
  try { localStorage.setItem(SLOTS_KEY, JSON.stringify(slots)); } catch { /* noop */ }
}

export function loadActiveSlotIndex(): number {
  try {
    const raw = localStorage.getItem(ACTIVE_SLOT_KEY);
    const n = raw ? parseInt(raw, 10) : 0;
    return isNaN(n) || n < 0 || n > 3 ? 0 : n;
  } catch {
    return 0;
  }
}

export function saveActiveSlotIndex(index: number): void {
  try { localStorage.setItem(ACTIVE_SLOT_KEY, String(index)); } catch { /* noop */ }
}
